import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { postReq } from "../utils/request";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { filterNavByPermissions } from "../utils/navPermissions";
import { menuItems } from "../layouts/Sidebar";
import { getRecentlyViewed, recordVisit, RecentRecord } from "../utils/recentlyViewed";
import { OPEN_COMMAND_PALETTE_EVENT } from "../utils/shortcuts";
import { useAiAccess } from "../hooks/useAiAccess";
import { looksLikeQuestion, openAssistant, openForm } from "../utils/assistant";
import { useAiCapabilities } from "../hooks/useAiCapabilities";
import { AskAction, matchActions, messageCost, topActions } from "../utils/askActions";
import { resumeAsk, waitingNote } from "../utils/askReturn";
import { useAskWaiting } from "../hooks/useAskWaiting";
import SearchSummaryBanner from "./SearchSummaryBanner";
import SearchSuggestions from "./SearchSuggestions";
import { highlightMatch, MatchChipList } from "./search/SearchResultRow";
import {
    CATEGORY_CONFIG,
    CATEGORY_ORDER,
    MatchContextChip,
    matchChips,
    SearchCategory,
    SearchResponseBody,
    SearchResultItem,
    SearchSuggestion,
    SearchSummary,
    subgroupResults,
} from "../constants/searchCategories";
import classes from "./commandPalette.module.scss";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/**
 * A flat, keyboard-navigable row. Rendering is grouped, but selection is flat —
 * one index walks every row regardless of which group it's in.
 */
interface PaletteRow {
    key: string;
    group: string;
    label: string;
    sublabel?: string;
    /** Why the search surfaced this row — rendered as chips beside the label. */
    chips?: MatchContextChip[];
    /** Bootstrap icon class for records; nav rows carry their own JSX icon. */
    icon?: string;
    iconElement?: JSX.Element;
    url: string;
    /** Set for record rows so opening one updates Recent. */
    record?: Omit<RecentRecord, "at">;
    /** The "Ask CRX" row: opens the assistant with the typed question. */
    ask?: boolean;
    /** A "Do" row or Open Ask: runs instead of navigating. */
    run?: () => void;
}

/** Where a row leads, for a real link (new tab, right-click menu) — "" for Do / Ask rows. */
const linkOf = (row: PaletteRow): string => (row.run || row.ask ? "" : row.url);

/**
 * Ctrl+K (⌘K on Mac) command palette: search everything, jump to any page you
 * have access to, and pick up whatever you were just looking at — without
 * leaving the keyboard.
 *
 * The destinations come from the SAME nav manifest and permission filter the
 * sidebar uses, so the palette can never offer a page the sidebar hides.
 * Searching hits the same gated `extra/quick-search` endpoint, so it can never
 * surface a record the user isn't allowed to see. For roles with AI on, an
 * "Ask CRX" row (first when the text reads like a question) opens the
 * assistant beside the page with the question sent.
 *
 * "Do or ask" (roadmap #31): "Do" rows start what the role allows (New task,
 * Ask for leave …), matched by code — free; doing words put them first.
 * Shift+Enter opens Ask with the words unsent; an empty box offers Open Ask.
 */
const CommandPalette: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useUserPermissions() as any;
    const { access: aiAccess } = useAiAccess();
    const canAsk = !!aiAccess?.available;
    const { waiting } = useAskWaiting();

    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchResultItem[]>([]);
    const [summary, setSummary] = useState<SearchSummary | null>(null);
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [recents, setRecents] = useState<RecentRecord[]>([]);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    // Guards against a slow response for an old query overwriting a newer one.
    const requestSeq = useRef(0);

    const userPages = currentUser?.pages || [];
    const capabilities = useAiCapabilities(null, canAsk && isOpen);

    const doRowOf = useCallback((action: AskAction): PaletteRow => ({
        key: `do:${action.key}`,
        group: "Do",
        label: action.label,
        sublabel: action.note ?? "Opens the form · no AI credit used",
        icon: `bi-${action.icon}`,
        url: "",
        run: () => {
            if (action.form) openForm(action.form);
            else if (action.start) action.start();
            else if (action.to) navigate(action.to);
        },
    }), [navigate]);

    // Every destination this user may reach — one source of truth with the sidebar.
    const navRows: PaletteRow[] = useMemo(() => {
        return filterNavByPermissions(menuItems, userPages)
            .flatMap(category =>
                category.items
                    // Static downloads (the PDF manual) aren't navigable destinations.
                    .filter(item => !item.download)
                    .map(item => ({
                        key: `nav:${item.to}`,
                        group: "Go to",
                        label: item.label,
                        sublabel: category.category,
                        iconElement: item.iconComponent,
                        url: item.to,
                    })),
            );
    }, [userPages]);

    const open = useCallback(() => {
        setQuery("");
        setResults([]);
        setActiveIndex(0);
        setRecents(getRecentlyViewed());
        setIsOpen(true);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);

    // Ctrl+K / ⌘K from anywhere. Also intercepts the browser's own Ctrl+K.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setIsOpen(prev => {
                    if (prev) return false;
                    setQuery("");
                    setResults([]);
                    setActiveIndex(0);
                    setRecents(getRecentlyViewed());
                    return true;
                });
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // Clicking a hint badge opens the palette too. The hints live in the header
    // and the sidebar, which have no shared context with this component — an
    // event keeps them decoupled rather than forcing a provider around the app.
    useEffect(() => {
        window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, open);
        return () => window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, open);
    }, [open]);

    // Debounced search. Short queries return nothing rather than pulling the
    // whole database back on the first keystroke.
    useEffect(() => {
        if (!isOpen) return;

        const trimmed = query.trim();
        if (trimmed.length < MIN_QUERY_LENGTH) {
            setResults([]);
            setSummary(null);
            setSuggestions([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        const seq = ++requestSeq.current;

        const timer = setTimeout(async () => {
            // Results and completions for the word being typed, side by side.
            const [response, suggested] = await Promise.all([
                postReq<SearchResponseBody>("extra/quick-search", { text: trimmed }),
                postReq<{ suggestions: SearchSuggestion[] }>("extra/search-suggest", { text: query }).catch(() => null),
            ]);
            // A slower earlier request must not clobber a newer one.
            if (seq !== requestSeq.current) return;

            const ok = response?.status === 200;
            setResults(ok ? (response.body?.data ?? []) : []);
            setSummary(ok ? (response.body?.summary ?? null) : null);
            setSuggestions(suggested?.status === 200 ? (suggested.body?.suggestions ?? []) : []);
            setIsSearching(false);
            setActiveIndex(0);
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [query, isOpen]);

    // Build the visible rows: nav matches first (they're the cheap, certain hits),
    // then records grouped by category. With no query, offer Recent then everything.
    const rows: PaletteRow[] = useMemo(() => {
        const trimmed = query.trim().toLowerCase();

        if (!trimmed) {
            const recentRows: PaletteRow[] = recents.map(record => ({
                key: `recent:${record.type}:${record.id}`,
                group: "Recent",
                label: record.label,
                sublabel: record.sublabel,
                icon: CATEGORY_CONFIG[record.type]?.icon ?? "bi-clock-history",
                url: record.url,
                record,
            }));
            // Ask waiting on an answer: "Back to Ask" comes first — the same answer, not a new chat
            const resumeRows: PaletteRow[] = canAsk && waiting ? [{
                key: "resume-ask", group: "Ask is waiting", label: "Back to Ask",
                sublabel: `${waiting.title ? `${waiting.title} · ` : ""}${waitingNote(waiting)} · no AI credit used`,
                iconElement: <i className="bi bi-stars" />, url: "", run: resumeAsk,
            }] : [];
            const startRows: PaletteRow[] = canAsk ? [
                ...(waiting ? [] : [{
                    key: "open-ask", group: "Assistant", label: "Open Ask", sublabel: "No AI credit used until you send a question",
                    iconElement: <i className="bi bi-stars" />, url: "", run: () => openAssistant(),
                }]),
                ...topActions(userPages, capabilities).map(doRowOf),
            ] : [];
            return [...resumeRows, ...recentRows, ...startRows, ...navRows];
        }

        const matchingNav = navRows.filter(row => row.label.toLowerCase().includes(trimmed));
        const { actions, isAction } = matchActions(query, userPages);
        const doRows = actions.map(doRowOf);
        const fillsForm = isAction && actions.some((action) => action.fillable);

        // Group headings carry the "why" sub-heading when the query was
        // understood: "Organizations — Offers Medical Evacuation — not in Jordan".
        const resultRows: PaletteRow[] = CATEGORY_ORDER.flatMap((category: SearchCategory) =>
            subgroupResults(results.filter(item => item.type === category)).flatMap(sub =>
                sub.items.map(item => ({
                    key: `result:${category}:${item.id}:${item.url}`,
                    group: sub.group
                        ? `${CATEGORY_CONFIG[category].label} — ${sub.group}`
                        : CATEGORY_CONFIG[category].label,
                    label: item.name || item.referenceNumber || "(untitled)",
                    sublabel: [item.referenceNumber, item.parent, item.email].filter(Boolean).join(" · ") || undefined,
                    // Own chips, not part of the sublabel: when a search ranks
                    // an org whose name doesn't contain the query, these are
                    // what explain it — they need to stand out.
                    chips: matchChips(item),
                    icon: CATEGORY_CONFIG[category].icon,
                    url: item.url,
                    record: {
                        type: category,
                        id: item.id,
                        label: item.name || item.referenceNumber || "(untitled)",
                        sublabel: item.parent || undefined,
                        url: item.url,
                    },
                })),
            ),
        );

        const found = isAction ? [...matchingNav, ...resultRows] : [...matchingNav, ...resultRows, ...doRows];
        if (!canAsk || trimmed.length < MIN_QUERY_LENGTH) return isAction ? [...doRows, ...found] : found;
        const askRow: PaletteRow = {
            key: "ask",
            group: "Assistant",
            label: fillsForm ? "Fill it in with Ask" : "Ask CRX",
            sublabel: `“${query.trim()}” · ${messageCost(aiAccess)}`,
            iconElement: <i className="bi bi-stars" />,
            url: "",
            ask: true,
        };
        // Doing words: the free actions first, then "Fill it in with Ask".
        if (isAction) return [...doRows, askRow, ...found];
        const isQuestion = looksLikeQuestion(query);
        // A record search waits for its results, so a quick Enter can't ask by accident.
        if (!isQuestion && isSearching) return found;
        return isQuestion || found.length === 0 ? [askRow, ...found] : [...found, askRow];
    }, [query, results, recents, navRows, canAsk, isSearching, userPages, capabilities, doRowOf, aiAccess, waiting]);

    const openRow = useCallback((row: PaletteRow) => {
        if (row.run) {
            setIsOpen(false);
            row.run();
            return;
        }
        if (row.ask) {
            setIsOpen(false);
            openAssistant(query.trim(), { send: true });
            return;
        }
        if (row.record) {
            recordVisit(row.record);
        }
        setIsOpen(false);
        navigate(row.url);
    }, [navigate, query]);

    const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex(prev => (rows.length === 0 ? 0 : (prev + 1) % rows.length));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(prev => (rows.length === 0 ? 0 : (prev - 1 + rows.length) % rows.length));
        } else if (event.key === "Enter" && event.shiftKey && canAsk && query.trim()) {
            // Shift+Enter: Ask opens with the words in its box, unsent (no message used).
            event.preventDefault();
            setIsOpen(false);
            openAssistant(query.trim());
        } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            // Ctrl/⌘+Enter: the record or page in a new tab; the palette stays open for the next one.
            event.preventDefault();
            const row = rows[activeIndex];
            if (row && linkOf(row)) {
                if (row.record) recordVisit(row.record);
                window.open(linkOf(row), "_blank", "noopener");
            }
        } else if (event.key === "Enter") {
            event.preventDefault();
            const row = rows[activeIndex];
            if (row) openRow(row);
        }
    };

    // Keep the highlighted row in view while arrowing through a long list.
    useEffect(() => {
        const active = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
        active?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // Doing words or a question with no records found: hide the record search's
    // "understood / no matches" notes; the Do and Ask rows say what happens.
    const quietSearch = results.length === 0
        && (matchActions(query, userPages).isAction || (canAsk && looksLikeQuestion(query)));

    let renderedGroup: string | null = null;

    return (
        <Modal
            show={isOpen}
            onHide={close}
            onEntered={() => inputRef.current?.focus()}
            contentClassName={classes.paletteContent}
            dialogClassName={classes.paletteDialog}
        >
            <div className={classes.searchRow}>
                <i className={`bi bi-search ${classes.searchIcon}`} aria-hidden="true" />
                <input
                    ref={inputRef}
                    className={classes.searchInput}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onInputKeyDown}
                    placeholder={canAsk ? "Search, jump to a page, or ask CRX…" : "Search records, or jump to a page…"}
                    aria-label={canAsk ? "Search, jump to a page, or ask CRX" : "Search records, or jump to a page"}
                />
                {isSearching && <span className={classes.spinner} aria-label="Searching" />}
                <kbd className={classes.escHint}>Esc</kbd>
            </div>

            <div className={classes.list} ref={listRef}>
                {!isSearching && !quietSearch && query.trim().length >= MIN_QUERY_LENGTH && (
                    <>
                        <SearchSuggestions suggestions={suggestions} onPick={(text) => { setQuery(text); setActiveIndex(0); }} />
                        <SearchSummaryBanner
                            summary={summary}
                            compact
                            onPick={(text) => { setQuery(text); setActiveIndex(0); }}
                            onOpen={(url) => { setIsOpen(false); navigate(url); }}
                        />
                    </>
                )}

                {isSearching && rows.length === 0 && (
                    <div className={classes.status}>
                        <span className={classes.statusText}>Searching…</span>
                    </div>
                )}

                {!isSearching && rows.length === 0 && (
                    <div className={classes.status}>
                        <i className={`bi bi-search ${classes.statusIcon}`} aria-hidden="true" />
                        <span className={classes.statusTitle}>
                            {query.trim().length < MIN_QUERY_LENGTH
                                ? "Type to search"
                                : `Nothing found for “${query.trim()}”`}
                        </span>
                        <span className={classes.statusText}>
                            Names, reference numbers, emails, phones — or a page to jump to.
                        </span>
                    </div>
                )}

                {rows.map((row, index) => {
                    const isNewGroup = row.group !== renderedGroup;
                    renderedGroup = row.group;

                    return (
                        <React.Fragment key={row.key}>
                            {isNewGroup && <div className={classes.groupHeading}>{row.group}</div>}
                            <a
                                data-index={index}
                                href={linkOf(row) || undefined}
                                role={linkOf(row) ? undefined : "button"}
                                tabIndex={-1}
                                className={`${classes.row} ${index === activeIndex ? classes.rowActive : ""} ${row.ask || row.key === "open-ask" ? classes.rowAsk : ""}`}
                                title={row.run && row.group === "Do" ? (row.key.startsWith("do:report:") ? "Opens the report" : "Opens the form; nothing is saved until you press Save") : undefined}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={(event) => {
                                    // Ctrl / ⌘ / Shift-click: the browser opens the link in a new tab or window.
                                    if (linkOf(row) && (event.ctrlKey || event.metaKey || event.shiftKey)) {
                                        if (row.record) recordVisit(row.record);
                                        return;
                                    }
                                    event.preventDefault();
                                    openRow(row);
                                }}
                            >
                                <span className={classes.rowIcon} aria-hidden="true">
                                    {row.iconElement ?? <i className={`bi ${row.icon}`} />}
                                </span>
                                <span className={classes.rowLabel}>{row.ask || row.run ? row.label : highlightMatch(row.label, query)}</span>
                                {row.chips && row.chips.length > 0 && (
                                    <span className={classes.rowChips}>
                                        <MatchChipList item={{ matchContext: row.chips }} max={2} />
                                    </span>
                                )}
                                {row.sublabel && (
                                    <span className={classes.rowSublabel}>{row.sublabel}</span>
                                )}
                                {index === activeIndex && (
                                    <i className={`bi bi-arrow-return-left ${classes.rowEnter}`} aria-hidden="true" />
                                )}
                            </a>
                        </React.Fragment>
                    );
                })}
            </div>

            <div className={classes.footer}>
                <span className={classes.footerHint}><kbd>↑</kbd><kbd>↓</kbd><span className={classes.footerLabel}>move</span></span>
                <span className={classes.footerHint}><kbd>Enter</kbd><span className={classes.footerLabel}>{canAsk ? "open or ask" : "open"}</span></span>
                <span className={classes.footerHint}><kbd>Ctrl</kbd><kbd>Enter</kbd><span className={classes.footerLabel}>new tab</span></span>
                {canAsk && <span className={classes.footerHint}><kbd>Shift</kbd><kbd>Enter</kbd><span className={classes.footerLabel}>edit in Ask</span></span>}
                <span className={classes.footerHint}><kbd>Esc</kbd><span className={classes.footerLabel}>close</span></span>
            </div>
        </Modal>
    );
};

export default CommandPalette;
